// `evidence/source` — the Evidence banner's summary-suggestion read, carved from
// `src/hooks/query/useQueryEvidenceSummarySuggestion.ts` (now a bodied
// delegate). GRO-2767.
//
// S1: transport moved to `../api/sources` on the `@heidi/api/client` registry.
//
// S-enabled (cohort S2): the pre-move gate was `!!token && !!sessionId && enabled`.
// The token is the only app term, so it collapses into the delegate's boolean.
//
// THE `sessionId` TERM STAYS HERE, and the distinction is the reusable part
// (the same one `sourceListQueries.ts` records): `!!sessionId` is about the
// ARGUMENT, not about the app. Hoisting it to the delegate would let a caller
// pass `enabled: true` with an empty id and fire a GET at
// `/evidence//summary-suggestion`. The two terms are `&&`-ed in the legacy
// order, which costs nothing to preserve.
//
// `staleTime: Infinity` is frozen behaviour, not a default: the score is
// computed once per session and the banner must not re-request it on remount.
import { useQuery } from '@tanstack/react-query';

import {
  EVIDENCE_SUMMARY_SUGGESTION_QUERY_KEY,
  type EvidenceSummarySuggestionResponse,
} from '../../contracts';
import { getEvidenceSummarySuggestion } from '../api/sources';

export interface EvidenceSummarySuggestionReadOptions {
  sessionId: string;
  /**
   * App-resolved gate, already reduced to one decision — the caller's own
   * `enabled` argument AND-ed with the auth token by the delegate. The
   * `!!sessionId` half is applied here; see the header.
   */
  enabled: boolean;
}

export function useQueryEvidenceSummarySuggestion({
  sessionId,
  enabled,
}: EvidenceSummarySuggestionReadOptions) {
  return useQuery<EvidenceSummarySuggestionResponse>({
    queryKey: [EVIDENCE_SUMMARY_SUGGESTION_QUERY_KEY, sessionId],
    queryFn: () => getEvidenceSummarySuggestion(sessionId),
    enabled: enabled && !!sessionId,
    staleTime: Infinity,
  });
}
